/**
 * Byte counts arrive from the API as strings, because JSON has no BigInt and
 * rounding them through Number would corrupt values above 2^53 silently. Parsing
 * happens here, once, at the point of display.
 */
export function toNumber(value: string | number | bigint | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Binary units, because that is what `free`, `df` and Docker report. */
export function bytes(value: string | number | null | undefined, digits = 1): string {
  const n = toNumber(value);
  if (n === null) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  const i = Math.min(Math.floor(Math.log(Math.abs(n)) / Math.log(1024)), units.length - 1);
  const scaled = n / 1024 ** i;
  // Whole numbers for bytes and KiB: "1.0 B" reads as a rounding error.
  return `${scaled.toFixed(i < 2 ? 0 : digits)} ${units[i]}`;
}

export function percent(value: string | number | null | undefined, digits = 0): string {
  const n = toNumber(value);
  return n === null ? '—' : `${n.toFixed(digits)}%`;
}

/** Ratio of two byte counts as a percentage, or null when either is missing. */
export function ratio(
  used: string | number | null | undefined,
  total: string | number | null | undefined,
): number | null {
  const u = toNumber(used);
  const t = toNumber(total);
  if (u === null || t === null || t === 0) return null;
  return (u / t) * 100;
}

export function duration(seconds: string | number | null | undefined): string {
  const n = toNumber(seconds);
  if (n === null) return '—';
  const d = Math.floor(n / 86400);
  const h = Math.floor((n % 86400) / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** "3 minutes ago". Absolute timestamps are kept in the title attribute. */
export function ago(iso: string | Date | null | undefined): string {
  if (!iso) return 'never';
  const then = typeof iso === 'string' ? new Date(iso) : iso;
  const secs = Math.floor((Date.now() - then.getTime()) / 1000);
  if (secs < 0) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export function shortTime(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
